import {
  BaseDataGrid,
  ButtonRow,
  useModal,
  getFilteredColDefs,
  DataGridPage,
} from "@synota-io/synota-shared-ui";
import { Button, Tooltip } from "@mui/material";
import { PriceCheckOutlined, ReplyAll } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useBluePenguinUsers, IBluePenguinUser } from "../../../api/hooks/useBluePenguinUsers";
import { bluePenguinUsersFields } from "../fields/bpUserFields";
import { GridColumnVisibilityModel, GridInputRowSelectionModel } from "@mui/x-data-grid";
import { BpNodesBreadcrumbs } from "../components/BpNodesBreadcrumbs";
import { BpInviteFormModal } from "../modals/BpInviteFormModal";

export const BluePenguinUsersPage = () => {
  const { bpUsers, isLoading } = useBluePenguinUsers({});
  const inviteModal = useModal<{ bp_customer_id?: string; sandbox?: boolean }>();
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const columns = useMemo(() => {
    const defaultColumns = getFilteredColDefs(bluePenguinUsersFields, "table").map((column) => ({
      ...column,
      flex: 1,
    }));

    defaultColumns.push({
      field: "actions",
      headerName: "Actions",
      type: "actions",
      flex: 1,
      renderCell: ({
        row,
      }: {
        row: IBluePenguinUser & { onReinvite?: (bp_customer_id: string, sandbox: boolean) => void };
      }) => (
        <ButtonRow>
          {row.customer_status === "Prospect" ? (
            <Tooltip title="Re-send invitation">
              <Button
                onClick={() => {
                  inviteModal.onOpen({ bp_customer_id: row.bp_customer_id, sandbox: row.sandbox });
                }}
                size="small"
                color="primary"
                sx={{ minWidth: 0 }}
              >
                <ReplyAll fontSize="small" />
              </Button>
            </Tooltip>
          ) : null}
        </ButtonRow>
      ),
    });

    return defaultColumns;
  }, [inviteModal]);

  return (
    <DataGridPage
      key={String(isLoading)}
      title={<BpNodesBreadcrumbs />}
      actions={() => (
        <ButtonRow>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PriceCheckOutlined />}
            title="Create KYB Invite"
            onClick={() => inviteModal.onOpen({})}
            aria-label="Create KYB Invite"
          >
            Create KYB Invite
          </Button>
        </ButtonRow>
      )}
    >
      <BaseDataGrid
        rows={bpUsers}
        getRowId={(row: IBluePenguinUser) => `${row.synota_email}_${row.bp_customer_id}`}
        columns={columns}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={setColumnVisibility}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        loading={isLoading}
        autosizeOnMount
        density="standard"
      />
      {inviteModal.open ? (
        <BpInviteFormModal
          {...inviteModal}
          onClose={() => {
            inviteModal.onClose();
            inviteModal.setState({});
          }}
        />
      ) : null}
    </DataGridPage>
  );
};
